/**
 * Management API client for the console.
 *
 * Wraps the frozen management contract with Idempotency-Key on mutations,
 * If-Match on mutable resources, and bounded pagination. Kept deliberately
 * thin: the console cannot bypass server lifecycle semantics.
 */

export interface ApiRequestOptions {
  readonly method: "GET" | "POST" | "PATCH" | "DELETE";
  readonly path: string;
  readonly body?: unknown;
  readonly idempotencyKey?: string;
  readonly ifMatch?: string;
  readonly signal?: AbortSignal;
}

export interface ApiEnvelope<T> {
  readonly status: number;
  readonly data: T;
  readonly headers: Record<string, string>;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

export class ManagementApi {
  readonly baseUrl: string;
  #token: string | null = null;

  constructor(baseUrl: string = "/management/v1") {
    this.baseUrl = baseUrl.replace(/\/v1\/?$/u, "");
  }

  /** Set the bearer token after login/step-up. */
  setToken(token: string): void {
    this.#token = token;
  }

  async request<T>(options: ApiRequestOptions): Promise<ApiEnvelope<T>> {
    const headers: Record<string, string> = {};
    if (this.#token !== null) headers.authorization = `Bearer ${this.#token}`;
    if (options.body !== undefined) headers["content-type"] = "application/json";
    if (options.idempotencyKey !== undefined) headers["idempotency-key"] = options.idempotencyKey;
    if (options.ifMatch !== undefined) headers["if-match"] = options.ifMatch;
    const response = await fetch(`${this.baseUrl}${options.path}`, {
      method: options.method,
      headers,
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
      ...(options.signal === undefined ? {} : { signal: options.signal }),
    });
    const text = await response.text();
    const data = text === "" ? (null as T) : (JSON.parse(text) as T);
    if (!response.ok) {
      const err = (data as { code?: string; title?: string } | null) ?? {};
      throw new ApiError(response.status, err.code ?? "INTERNAL", err.title ?? response.statusText);
    }
    return {
      status: response.status,
      data,
      headers: unfoldHeaders(response.headers),
    };
  }

  healthz(): Promise<ApiEnvelope<{ status: string }>> {
    return this.request<{ status: string }>({ method: "GET", path: "/v1/healthz" });
  }

  listDecisions(limit = 50, cursor?: string): Promise<ApiEnvelope<{ data: DecisionView[]; nextCursor: string | null; total: number }>> {
    const params = new URLSearchParams({ limit: String(limit) });
    if (cursor !== undefined) params.set("cursor", cursor);
    return this.request({ method: "GET", path: `/v1/decisions?${params.toString()}` });
  }
}

export interface DecisionView {
  readonly id: string;
  readonly state: string;
  readonly reasonCode: string;
  readonly subject?: string;
  readonly subjectId?: string;
  readonly decidedAt?: number;
}

function unfoldHeaders(headers: Headers): Record<string, string> {
  const out: Record<string, string> = {};
  headers.forEach((value, key) => {
    out[key] = value;
  });
  return out;
}
